import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  deleteDocument,
  getPreMatchedFoundItemsAsyncThunk,
  getReportAsyncThunk,
  passPendingRequest,
  rejectPendingRequest,
  updateItemDetail,
  updateLocationDetail,
  updateUserDetail,
  uploadDocument,
  getReportVersionAsyncThunk,
  updateLostItemByAdmin,
  linkFoundItemWithLostItemAsyncThunk,
  deleteLostItem,
  getAllShippingOptions,
  updateShippingOptions,
  getPackage,
  updatePackage,
  sendPaymentRequest,
  updateLostItemCaseManager,
  uploadShippingLabel,
  uploadAdditionalDoc,
  updateTrackingNumber,
  createLostItemByAdmin,
  updateLostItemIsUrgent,
  uploadShippingProof,
  updateShippingOptionAddress,
  cloneShippingOptions,
} from "./reportAsyncThunk";
import { Document, Report, User } from "../../../types";
import { toast } from "react-toastify";
import { Package, ShippingOption } from "../../../types/ShipTypes";
import { AdminUser } from "../user/types";
import { PrematchedFoundItems } from "../../../types/PreMatchedFoundItems";
import { LostStatuses } from "../../../types/LostStatuses";
import { updateLostItemDate } from "../board/columnsAsyncThunk";
import { shipMatchEnabled } from "../../../utils/constants";

export interface ReportState {
  report: Report | null;
  loading: boolean;
  error: string | null;
  shippingOptions: ShippingOption[] | null;
  shippingOptionsLoading: boolean;
  shippingOptionsError: string | null;
  package: Package | null;
  packageLoading: boolean;
  packageError: string | null;
  preMatchedFoundItems: PrematchedFoundItems[] | null;
  versionData: Report | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  paymentRequestLoading: boolean;
  paymentRequestError: string | null;
  prematchModal: {
    isOpen: boolean;
  } | null;
  updateShippingOptionAddressLoading: boolean;
}

const initialState: ReportState = {
  report: null,
  loading: false,
  error: null,
  shippingOptions: null,
  shippingOptionsLoading: false,
  shippingOptionsError: null,
  package: null,
  packageLoading: false,
  packageError: null,
  preMatchedFoundItems: null,
  versionData: null,
  status: "idle",
  paymentRequestLoading: false,
  paymentRequestError: null,
  prematchModal: null,
  updateShippingOptionAddressLoading: false,
};

interface SetKeyValuePayload<K extends keyof ReportState> {
  key: K;
  value: ReportState[K];
}

const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    setKeyValue: <K extends keyof ReportState>(state: ReportState, action: PayloadAction<SetKeyValuePayload<K>>) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
    clearVersionData: (state) => {
      state.versionData = null;
    },
    clearReport: (state) => {
      state.report = null;
      state.loading = false;
      state.error = null;
    },
    clearShippingOptions: (state) => {
      state.shippingOptions = null;
      state.shippingOptionsLoading = false;
      state.shippingOptionsError = null;
    },
    clearPackage: (state) => {
      state.package = null;
      state.packageLoading = false;
      state.packageError = null;
    },
    updateCaseManager: (state, action: PayloadAction<{ caseManager: AdminUser | undefined }>) => {
      if (state?.report && action?.payload?.caseManager) {
        state.report.caseManager = { ...action.payload.caseManager };
        state.report.caseManagerName = `${action.payload.caseManager.firstName} ${action.payload.caseManager.lastName}`;
      }
    },
    setPrematchModal: (state, action: PayloadAction<{ isOpen: boolean }>) => {
      state.prematchModal = { ...action.payload };
    },
    updateReportStatus: (state, action: PayloadAction<{ status: number }>) => {
      if (state?.report?.id) {
        const isReverseStatusChange = action.payload.status < state.report.status;
        const isReverseToInProcess = action.payload.status === LostStatuses.InProcess;

        if (isReverseStatusChange && (isReverseToInProcess)) {
          state.report.shippingLabel = undefined;
          state.report.trackingNumber = "";
          state.report.additionalDocs = [];
        }
        if (isReverseStatusChange) {
          state.report.payment = [];
          state.report.paymentReceipt = [];
        }
        if (isReverseStatusChange && (isReverseToInProcess)) {
          state.report.shippingOptions = [];
          state.package = null;
          state.report.foundItem = null;
        }
        state.report.status = action.payload.status;
        if (action.payload.status === LostStatuses.Deleted) {
          state.report = null;
        }
      }
    },
    updateReportFoundItem: (state, action: PayloadAction<{ foundItem: PrematchedFoundItems | null }>) => {
      if (state?.report?.id) {
        state.report.foundItem = action.payload.foundItem || null;
      }
    },
    updateReportLFNotifications: (state, action: PayloadAction<{
      lfDepartmentName?: string;
      lfContactPerson?: string;
      lfContactEmail?: string;
      lfContactPhone?: string;
      lfNotes?: string;
      additionalComments?: string;
      establishmentName?: string;
      lfNotifiedAt?: string;
      lfNotifications?: any[];
    }>) => {
      if (state?.report?.id) {
        Object.assign(state.report, action.payload);
      }
    },

  },
  extraReducers: (builder) => {
    builder
      //reportAsyncThunk
      .addCase(getReportAsyncThunk.pending, (state) => {
        state.status = "loading";
        state.report = null;
        state.versionData = null;
        state.error = null;
      })
      .addCase(getReportAsyncThunk.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.report = action.payload;
      })
      .addCase(getReportAsyncThunk.rejected, (state, action) => {
        state.status = "failed";
        toast.error(action.payload || "Failed to get Report Data.");
        state.error = action.payload || "An error occurred";
      })

      //Update user details
      .addCase(updateUserDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserDetail.fulfilled, (state, action: PayloadAction<User>) => {
        if (state.report) {
          state.report.user = { ...action?.payload };
        }
        state.loading = false;
        toast.success("Personal information updated successfully");
      })
      .addCase(updateUserDetail.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to update Personal information.");
        state.error = action.payload || "Failed to update Personal information.";
      })
      //Update Item Details
      .addCase(updateItemDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateItemDetail.fulfilled, (state, action) => {
        if (state.report) {
          state.report = { ...state.report, ...action.payload };
        }
        state.loading = false;
        // toast.success("Item details updated successfully");
      })
      .addCase(updateItemDetail.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to update item details.");
        state.error = action.payload || "Failed to update item details.";
      })
      // Update Location Details
      .addCase(updateLocationDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLocationDetail.fulfilled, (state, action) => {
        if (state.report) {
          state.report = { ...state.report, ...action.payload };
        }
        state.loading = false;
        toast.success("Location details updated successfully");
      })
      .addCase(updateLocationDetail.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to update location details.");
        state.error = action.payload || "Failed to update location details.";
      })
      // Upload Document
      .addCase(uploadDocument.fulfilled, (state, action) => {
        if (state.report) {
          state.report = { ...state.report, ...action.payload };
        }
        toast.success("Document uploaded successfully");
      })
      // Delete Document
      .addCase(deleteDocument.fulfilled, (state, action) => {
        if (state.report) {
          state.report.documents = state.report.documents.filter((document) => document.id !== action.payload);
        }
        toast.success("Document deleted successfully");
      })
      //Update Item as Urgent
      .addCase(updateLostItemIsUrgent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLostItemIsUrgent.fulfilled, (state, action) => {
        if (action.payload && state?.report?.id) {
          state.report = { ...state.report, isUrgent: action.payload?.isUrgent };
        }
        state.loading = false;
      })
      .addCase(updateLostItemIsUrgent.rejected, (state, action) => {
        state.loading = false;
        toast.error((action?.payload as string) || "Failed to update mark as urgent.");
        state.error = (action?.payload as string) || "Failed to update item details.";
      })
      //Pass pending request
      .addCase(passPendingRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(passPendingRequest.fulfilled, (state, action) => {
        if (action.payload && state?.report?.id && !action.payload.isDeleteRequest) {
          state.report = { ...state.report, isUrgent: action.payload?.isUrgent };
        } else if (action.payload && state?.report?.id && action.payload.isDeleteRequest) {
          state.report = null;
        }
        state.loading = false;
        // toast.success(`Item update passed successfully`);
      })
      .addCase(passPendingRequest.rejected, (state, action: any) => {
        state.loading = false;
        const errMsg = action?.payload || "Failed to pass the updated item details.";
        toast.error(errMsg);
        state.error = errMsg;
      })
      //Reject pending request
      .addCase(rejectPendingRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectPendingRequest.fulfilled, (state, action) => {
        if (action.payload && state?.report?.id) {
          state.report = { ...state.report, isUrgent: action.payload?.isUrgent, pendingRequest: undefined };
        }
        state.loading = false;
        // toast.success(`Request rejected successfully`);
      })
      .addCase(rejectPendingRequest.rejected, (state, action: any) => {
        state.loading = false;
        const errMsg = action?.payload || "Failed to reject request.";
        toast.error(errMsg);
        state.error = errMsg;
      })
      //Get PreMatched found items
      .addCase(getPreMatchedFoundItemsAsyncThunk.pending, (state) => {
        // state.status = "loading";
        state.error = null;
      })
      .addCase(getPreMatchedFoundItemsAsyncThunk.fulfilled, (state, action) => {
        // state.status = "succeeded";
        state.preMatchedFoundItems = action.payload;
      })
      .addCase(getPreMatchedFoundItemsAsyncThunk.rejected, (state, action) => {
        // state.status = "failed";
        toast.error(action.payload || "Failed to get pre matched Data.");
        state.error = action.payload || "An error occurred";
      })
      .addCase(getReportVersionAsyncThunk.pending, (state) => {
        state.loading = true;
        state.status = "loading";
        state.error = null;
      })
      .addCase(getReportVersionAsyncThunk.fulfilled, (state, action) => {
        state.versionData = action.payload;
        state.loading = false;
        state.status = "succeeded";
      })
      .addCase(getReportVersionAsyncThunk.rejected, (state, action) => {
        state.loading = false;
        state.status = "failed";
        state.error = action.payload || "Failed to fetch version data";
        toast.error(action.payload || "Failed to fetch version data");
      })
      // updateLostItemByAdmin
      .addCase(updateLostItemByAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLostItemByAdmin.fulfilled, (state, action) => {
        state.report = action.payload;
        state.loading = false;
        toast.success("Lost item updated successfully");
      })
      .addCase(updateLostItemByAdmin.rejected, (state, action) => {
        state.loading = false;
        const errorMsg = action.payload || "Failed to update lost item by admin.";
        toast.error(errorMsg);
        state.error = errorMsg;
      })
      // createLostItemByAdmin
      .addCase(createLostItemByAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLostItemByAdmin.fulfilled, (state, action) => {
        state.loading = false;
        toast.success("Lost item created successfully");
      })
      .addCase(createLostItemByAdmin.rejected, (state, action) => {
        state.loading = false;
        const errorMsg = action.payload || "Failed to create lost item by admin.";
        toast.error(errorMsg);
        state.error = errorMsg;
      })
      // linkFoundItemWithLostItem
      .addCase(linkFoundItemWithLostItemAsyncThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(linkFoundItemWithLostItemAsyncThunk.fulfilled, (state, action) => {
        // state.data = action.payload;
        state.loading = false;
        toast.success("Found item link with lost item successfully");
      })
      .addCase(linkFoundItemWithLostItemAsyncThunk.rejected, (state, action) => {
        state.loading = false;
        const errorMsg = "Failed to link found item with lost item";
        toast.error(action.payload || errorMsg);
        state.error = action.payload || errorMsg;
      })
      //Delete lost item
      .addCase(deleteLostItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteLostItem.fulfilled, (state, action) => {
        if (state.report) {
          state.report = null;
        }
        toast.success("Lost item deleted successfully");
        state.loading = false;
      })
      .addCase(deleteLostItem.rejected, (state, action) => {
        const errMsg = "Failed to delete lost item.";
        toast.error(action.payload || errMsg);
        state.loading = false;
      })
      //Get All Shipping Options
      .addCase(getAllShippingOptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllShippingOptions.fulfilled, (state, action: { payload: ShippingOption[] }) => {
        state.shippingOptions = action.payload;
        state.loading = false;
      })
      .addCase(getAllShippingOptions.rejected, (state, action: any) => {
        toast.error(action.payload || "Failed to get shipping options.");
        state.loading = false;
      })
      //Update Shipping Options
      .addCase(updateShippingOptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateShippingOptions.fulfilled, (state, action: { payload: ShippingOption }) => {
        if (state?.shippingOptions && state?.shippingOptions?.length > 0) {
          const findIndex = state.shippingOptions.findIndex((option) => option.id === action.payload.id);
          if (findIndex !== -1) {
            state.shippingOptions[findIndex] = { ...action.payload };
          }
        }
        state.loading = false;
        toast.success("Shipping options updated successfully");
      })
      .addCase(updateShippingOptions.rejected, (state, action: any) => {
        toast.error(action.payload || "Failed to update shipping options.");
        state.loading = false;
      })
      //Clone Shipping Options
      .addCase(cloneShippingOptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cloneShippingOptions.fulfilled, (state, action: { payload: ShippingOption }) => {
        if (state?.shippingOptions && state?.shippingOptions?.length > 0) {
          state.shippingOptions = [...state.shippingOptions, { ...action.payload }];
        }
        state.loading = false;
        toast.success("Shipping options cloned successfully");
      })
      .addCase(cloneShippingOptions.rejected, (state, action: any) => {
        toast.error(action.payload || "Failed to clone shipping options.");
        state.loading = false;
      })
      // Get Package
      .addCase(getPackage.pending, (state) => {
        state.packageLoading = true;
        state.packageError = null;
      })
      .addCase(getPackage.fulfilled, (state, action) => {
        state.package = action.payload;
        state.packageLoading = false;
      })
      .addCase(getPackage.rejected, (state, action) => {
        state.packageError = action.payload || "An error occurred";
        state.packageLoading = false;
      })
      // Update Package
      .addCase(updatePackage.pending, (state) => {
        state.packageError = null;
        state.packageLoading = true;
      })
      .addCase(updatePackage.fulfilled, (state, action) => {
        state.package = action.payload;
        toast.success("Package dimensions updated successfully");
        state.packageLoading = false;
      })
      .addCase(updatePackage.rejected, (state, action) => {
        state.packageLoading = false;
        state.packageError = action.payload || "An error occurred";
        toast.error(action.payload || "Failed to update package dimensions");
      })
      //Send Payment Request
      .addCase(sendPaymentRequest.pending, (state) => {
        state.paymentRequestLoading = true;
        state.paymentRequestError = null;
      })
      .addCase(sendPaymentRequest.fulfilled, (state, action: { payload: Report }) => {
        if (state?.report?.id && action.payload?.id) {
          state.report = action.payload;
          // state.report.foundItem.status = action.payload.foundItem?.status || 0;
        }
        state.paymentRequestLoading = false;
      })
      .addCase(sendPaymentRequest.rejected, (state, action) => {
        state.paymentRequestLoading = false;
        toast.error(action.payload || "Failed to send payment request");
      })
      //Update Lost Item Case Manager
      .addCase(updateLostItemCaseManager.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLostItemCaseManager.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateLostItemCaseManager.rejected, (state, action: any) => {
        toast.error(action.payload || "Failed to update case manager");
        state.loading = false;
      })
      //Upload Shipping Label
      .addCase(uploadShippingLabel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadShippingLabel.fulfilled, (state, action: any) => {
        if (state?.report?.id && action.payload?.isDelete) {
          state.report.shippingLabel = undefined;
        } else if (state?.report?.id) {
          state.report.shippingLabel = action.payload;
        }
        state.loading = false;
      })
      .addCase(uploadShippingLabel.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to upload shipping label");
      })
      //Upload Additional Document
      .addCase(uploadAdditionalDoc.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadAdditionalDoc.fulfilled, (state, action: { payload: { isDelete: boolean; documents: Document[] } }) => {
        if (state?.report?.id) {
          state.report.additionalDocs = action.payload.documents || undefined;
        }
        state.loading = false;
      })
      .addCase(uploadAdditionalDoc.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to upload additional document");
      })
      //Upload Shipping Proof
      .addCase(uploadShippingProof.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadShippingProof.fulfilled, (state, action: { payload: { isDelete: boolean; documents: Document[]; deliveryDate?: string } }) => {
        if (state?.report?.id) {
          state.report.shippingProof = action.payload.documents || undefined;
          state.report.deliveryDate = action.payload.deliveryDate;
        }
        state.loading = false;
      })
      .addCase(uploadShippingProof.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to upload shipping proof");
      })
      //Update Tracking Number
      .addCase(updateTrackingNumber.pending, (state) => {
        state.error = null;
      })
      .addCase(updateTrackingNumber.fulfilled, (state, action) => {
        if (state?.report?.id) {
          state.report.trackingNumber = action.payload?.trackingNumber;
        }
        state.loading = false;
      })
      .addCase(updateTrackingNumber.rejected, (state, action) => {
        toast.error(action.payload || "Failed to update tracking number");
        state.loading = false;
      })
      //Update Lost Item Date
      .addCase(updateLostItemDate.pending, (state) => {
        state.error = null;
      })
      .addCase(updateLostItemDate.fulfilled, (state, action) => {
        if (state?.report?.id) {
          state.report.lostDate = action.payload?.lostDate;
        }
        state.loading = false;
      })
      .addCase(updateLostItemDate.rejected, (state, action: any) => {
        toast.error(action.payload || "Failed to update lost item date");
        state.loading = false;
      })
      //Update Shipping Option Address
      .addCase(updateShippingOptionAddress.pending, (state) => {
        state.error = null;
        state.updateShippingOptionAddressLoading = true;
      })
      .addCase(updateShippingOptionAddress.fulfilled, (state, action: { payload: ShippingOption }) => {
        if (state?.report?.id) {
          if (state?.report?.shippingOptions && state?.report?.shippingOptions?.length > 0) {
            const findIndex = state.report.shippingOptions.findIndex((option: ShippingOption) => option.id === action.payload.id);
            if (findIndex !== -1) {
              state.report.shippingOptions[findIndex] = { ...state.report.shippingOptions[findIndex], ...action.payload };
            }
          }
        }
        state.updateShippingOptionAddressLoading = false;
      })
      .addCase(updateShippingOptionAddress.rejected, (state, action: any) => {
        toast.error(action.payload || "Failed to update shipping option address");
        state.updateShippingOptionAddressLoading = false;
      });
  },
});

export const { setKeyValue, clearVersionData, clearReport, clearShippingOptions, clearPackage, updateCaseManager, setPrematchModal, updateReportStatus, updateReportFoundItem, updateReportLFNotifications } = reportSlice.actions;
export default reportSlice.reducer;
